import axios from 'axios';
import { config } from '../config';

export const axiosInstance = axios.create({
  baseURL: config.apiUrl,
  timeout: 30000, // 30 seconds timeout
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// Initialize API logging preference (following businessAppApi.ts pattern)
export const initializeApiLogging = (enabled = false) => {
  // Only enable logging in development mode
  if (process.env.NODE_ENV === 'development') {
    localStorage.setItem('ENABLE_API_LOGGING', enabled ? 'true' : 'false');
    
    if (enabled) {
      console.log('🔄 API logging enabled. You will see detailed API request/response logs.');
    } else {
      console.log('🔄 API logging disabled. API logs will be minimal.');
    }
  }
};

// Default to disabled logging
if (process.env.NODE_ENV === 'development' && localStorage.getItem('ENABLE_API_LOGGING') === null) {
  localStorage.setItem('ENABLE_API_LOGGING', 'false');
}

// Add request interceptor for debugging (following businessAppApi.ts pattern)
axiosInstance.interceptors.request.use(
  (config) => {
    // Generate a request ID for logging only
    const requestId = Date.now().toString(36) + Math.random().toString(36).substring(2, 5);
    
    // Only log if explicitly set by environment variable or localStorage
    const shouldLog = localStorage.getItem('ENABLE_API_LOGGING') === 'true';
    
    // Enhanced logging for development without adding the header
    if (process.env.NODE_ENV === 'development' && shouldLog) {
      console.log(`🔄 [API Request ${requestId}] ${config.method?.toUpperCase()} ${config.url}`, {
        params: config.params,
        data: config.data
      });
    }
    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add authentication interceptor to include token in requests (following businessAppApi.ts pattern)
axiosInstance.interceptors.request.use(
  (config) => {
    // Get the auth token from localStorage
    try {
      // First try businessAuth, then fallback to auth for backwards compatibility
      const businessAuth = JSON.parse(localStorage.getItem('businessAuth') || '{}');
      const publicAuth = JSON.parse(localStorage.getItem('auth') || '{}');
      const auth = businessAuth.token ? businessAuth : publicAuth;
      const token = auth.token;
      
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      console.error('Error adding auth token to request:', error);
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging and error handling (following businessAppApi.ts pattern)
axiosInstance.interceptors.response.use(
  (response) => {
    // Generate a log ID for this response
    const logId = Date.now().toString(36);
    
    // Only log if explicitly set by environment variable or localStorage
    const shouldLog = localStorage.getItem('ENABLE_API_LOGGING') === 'true';
    
    // Enhanced logging for development
    if (process.env.NODE_ENV === 'development' && shouldLog) {
      console.log(`✅ [API Response ${logId}] ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`, {
        data: response.data
      });
    }
    
    return response;
  },
  async (error) => {
    // Log detailed error information
    if (error.response) {
      const logId = Date.now().toString(36);
      console.error(`❌ [API Error ${logId}] ${error.response.status} ${error.config.method?.toUpperCase()} ${error.config.url}`, {
        status: error.response.status,
        message: error.message,
        data: error.response.data,
        headers: error.response.headers
      });
    } else if (error.request) {
      console.error('❌ API Error - No Response:', { 
        request: error.request, 
        message: error.message 
      });
    } else {
      console.error('❌ API Setup Error:', error.message);
    }
    
    // Handle 401 errors gracefully
    if (error.response?.status === 401) {
      // Instead of immediately logging out, check if we can refresh the token
      const businessAuth = JSON.parse(localStorage.getItem('businessAuth') || '{}');
      const publicAuth = JSON.parse(localStorage.getItem('auth') || '{}');
      const auth = businessAuth.token ? businessAuth : publicAuth;
      
      if (!auth.token) {
        // Only redirect if there's no token at all
        window.location.href = '/';
      }
    }
    
    return Promise.reject(error);
  }
); 